/**
 * @file
 * The TabToggle class.
 */

/* global Tabs */

import { addClass, removeClass } from "@graupl/core/src/domHelpers.js";
import { TransactionalValue } from "../TransactionalValue.js";
import Component from "../Component.js";

class TabToggle extends Component {
  /**
   * The DOM elements within the tab toggle.
   *
   * @protected
   *
   * @type {Object<HTMLElement>}
   *
   * @property {HTMLElement} toggle  - The toggle element.
   * @property {HTMLElement} content - The content element.
   */
  _dom = {
    toggle: null,
    content: null,
  };

  /**
   * The DOM elements within the tab toggle that cannot be reset or generated by the tab toggle itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = ["toggle", "content"];

  /**
   * The instantiated elements within the component.
   *
   * @protected
   *
   * @type {Object<Tabs>}
   *
   * @property {Tabs} parent - The parent tabs element.
   */
  _elements = {
    parent: null,
  };

  /**
   * The active state of the tab toggle.
   *
   * @protected
   *
   * @type {TransactionalValue}
   */
  _active = new TransactionalValue(false);

  /**
   * Custom events that can be triggered throughout the tab toggle.
   *
   * @protected
   *
   * @type {Object<CustomEvent>}
   */
  _events = {
    /**
     * The event that is triggered when the tab toggle is shown.
     *
     * @event grauplTabToggleActivate
     *
     * @type {CustomEvent}
     *
     * @property {boolean}           bubbles       - A flag to bubble the event
     * @property {Object<TabToggle>} detail        - The details object containing the tab toggle itself.
     * @property {TabToggle}         detail.toggle - The tab toggle.
     */
    activate: new CustomEvent("grauplTabToggleActivate", {
      bubbles: true,
      detail: {
        toggle: this,
      },
    }),
    /**
     * The event that is triggered when the tab toggle is hidden.
     *
     * @event grauplTabToggleDeactivate
     *
     * @type {CustomEvent}
     *
     * @property {boolean}           bubbles       - A flag to bubble the event
     * @property {Object<TabToggle>} detail        - The details object containing the tab toggle itself.
     * @property {TabToggle}         detail.toggle - The tab toggle.
     */
    deactivate: new CustomEvent("grauplTabToggleDeactivate", {
      bubbles: true,
      detail: {
        toggle: this,
      },
    }),
  };

  /**
   * Constructs a new TabToggle.
   *
   * @param {object}      [options = {}]         - The options for generating the tab toggle.
   * @param {HTMLElement} options.toggleElement  - The tab toggle DOM element.
   * @param {HTMLElement} options.contentElement - The tab content DOM element.
   * @param {Tabs}        options.parentTab      - The Tabs element the toggle belongs to.
   */
  constructor({ toggleElement, contentElement, parentTab } = {}) {
    super({
      prefix: parentTab.prefix,
      key: parentTab.key,
    });

    // Set DOM elements.
    this._dom.toggle = toggleElement;
    this._dom.content = contentElement;

    // Set the parent tab instance.
    this._elements.parent = parentTab;
  }

  /**
   * Initializes the tab toggle.
   */
  initialize() {
    // Set up the DOM.
    this._setIds();
    this._setAriaAttributes();

    if (this.dom.toggle.getAttribute("aria-selected") === "true") {
      this.show({ force: true, emit: false, transition: false });
    } else {
      this.hide({ force: true, emit: false, transition: false });
    }
  }

  /**
   * The active state of the tab toggle.
   *
   * @readonly
   *
   * @type {TransactionalValue}
   *
   * @see _active
   */
  get isActive() {
    return this._active.value;
  }

  /**
   * Sets the IDs of the tab toggle and it's content if they do not already exist.
   *
   * The generated IDs use the following format:
   *   - toggle: `tab-toggle-${key}-${index}`
   *   - content: `tab-content-${key}-${index}`
   *
   * Where `${key}` is the parent's key and `${index}` is the toggles index in the list of the parent's toggles.
   *
   * @protected
   */
  _setIds() {
    // Get the required information for IDs.
    const { key } = this.elements.parent;
    const index = this.elements.parent.dom.tabToggle.indexOf(this.dom.toggle);

    this.dom.toggle.id = this.dom.toggle.id || `tab-toggle-${key}-${index}`;
    this.dom.content.id = this.dom.content.id || `tab-content-${key}-${index}`;

    this._id = this.dom.toggle.id;
  }

  /**
   * Sets the ARIA attributes on the disclosure and it's content.
   */
  _setAriaAttributes() {
    // Set the ARIA attributes for the tab item toggle.
    this.dom.toggle.setAttribute("role", "tab");

    // If aria-selected is not explicitly set to "true", then set it to "false".
    if (this.dom.toggle.getAttribute("aria-selected") !== "true") {
      this.dom.toggle.setAttribute("aria-selected", "false");
    }

    // Set the aria-controls attribute for the toggle.
    this.dom.toggle.setAttribute("aria-controls", this.dom.content.id);

    // Set the role for the content.
    this.dom.content.setAttribute("role", "tabpanel");
  }

  /**
   * Reveal the toggle.
   *
   * Sets the toggle's `aria-selected` to "true", adds the
   * open class to the content, and removes the closed class from the content.
   *
   * @protected
   *
   * @fires grauplTabToggleActivate
   *
   * @param {Object<boolean>} [options = {}]              - Options for revealing the toggle.
   * @param {boolean}         [options.emit = true]       - Emit the activate event once revealed.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _reveal({ emit = true, transition = true } = {}) {
    const { closeClass, openClass, transitionClass, openDuration } =
      this.elements.parent;

    // Set aria-selected to true when hiding accordion item.
    this.dom.toggle.setAttribute("aria-selected", "true");

    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the hide class,
    // add the show class, and finally remove the transition class.
    //
    // If `transition` is false, then it doesn't matter if the transition class
    // is set. Do not use the transition.
    if (transition && transitionClass !== "") {
      addClass(transitionClass, this.dom.content);

      requestAnimationFrame(() => {
        removeClass(closeClass, this.dom.content);

        requestAnimationFrame(() => {
          addClass(openClass, this.dom.content);

          requestAnimationFrame(() => {
            this._setTimeout(
              () => removeClass(transitionClass, this.dom.content),
              openDuration
            );
          });
        });
      });
    } else {
      // Add the show class
      addClass(openClass, this.dom.content);

      // Remove the hide class.
      removeClass(closeClass, this.dom.content);
    }

    if (emit) {
      this._dispatchEvent("activate", this.dom.toggle);
    }
  }

  /**
   * Conseals the toggle.
   *
   * Sets the toggle's `aria-expanded` to "false", adds the
   * close class to the content, and removes the open class from the content.
   *
   * @protected
   *
   * @fires grauplTabToggleDeactivate
   *
   * @param {Object<boolean>} [options = {}]              - Options for concealing the toggle.
   * @param {boolean}         [options.emit = true]       - Emit the deactivate event once concealed.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _conceal({ emit = true, transition = true } = {}) {
    const { closeClass, openClass, transitionClass, closeDuration } =
      this.elements.parent;

    // Set aria-selected to false when hiding accordion item.
    this.dom.toggle.setAttribute("aria-selected", "false");

    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the show class,
    // add the hide class, and finally remove the transition class.
    //
    // If `transition` is false, then it doesn't matter if the transition class
    // is set. Do not use the transition.
    if (transition && transitionClass !== "") {
      addClass(transitionClass, this.dom.content);

      requestAnimationFrame(() => {
        removeClass(openClass, this.dom.content);

        requestAnimationFrame(() => {
          addClass(closeClass, this.dom.content);

          requestAnimationFrame(() => {
            this._setTimeout(
              () => removeClass(transitionClass, this.dom.content),
              closeDuration
            );
          });
        });
      });
    } else {
      // Add the hide class
      addClass(closeClass, this.dom.content);

      // Remove the show class.
      removeClass(openClass, this.dom.content);
    }

    if (emit) {
      this._dispatchEvent("deactivate", this.dom.toggle);
    }
  }

  /**
   * Shows the tab toggle's content.
   *
   * @param {Object<boolean>} [options = {}]                  - The options for showing the tab toggle.
   * @param {boolean}         [options.force = false]         - Whether to force the open action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   * @param {boolean}         [options.emit = true]           - Emit the deactivate event once revealed.
   * @param {boolean}         [options.transition = true]     - Respect the transition class.
   */
  show({
    force = false,
    preserveState = false,
    emit = true,
    transition = true,
  } = {}) {
    if (this.isActive && !force) return;

    // Set the focus state of the parent tabs element.
    this.elements.parent.focusState = "self";

    // Reveal the toggle.
    this._reveal({ emit, transition });

    // Set the active state
    this._active.value = true;

    if (!preserveState) {
      this._active.commit();
    }

    // Set the tabindex to 0 so it can be focused.
    this.dom.toggle.setAttribute("tabindex", "0");

    // Hide all sibling tab toggles.
    this.hideSiblings();
  }

  /**
   * Hides the tab toggle's content.
   *
   * @param {Object<boolean>} [options = {}]                  - The options for hiding the tab toggle.
   * @param {boolean}         [options.force = false]         - Whether to force the hide action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   * @param {boolean}         [options.emit = true]           - Emit the deactivate event once consealed.
   * @param {boolean}         [options.transition = true]     - Respect the transition class.
   */
  hide({
    force = false,
    preserveState = false,
    emit = true,
    transition = true,
  } = {}) {
    if (!this.isActive && !force) return;

    // Set the focus state of the parent tabs element.
    this.elements.parent.focusState = "none";

    // Conceal the toggle.
    this._conceal({ emit, transition });

    // Set the active state
    this._active.value = false;

    if (!preserveState) {
      this._active.commit();
    }

    // Set the tabindex to -1.
    this.dom.toggle.setAttribute("tabindex", "-1");
  }

  /**
   * Focuses the accordion item.
   */
  focus() {
    this.dom.toggle.focus();
  }

  /**
   * Blurs the accordion item.
   */
  blur() {
    this.dom.toggle.blur();
  }

  /**
   * Hides all sibling toggles within the same Tabs element.
   */
  hideSiblings() {
    if (this.elements.parent) {
      this.elements.parent.elements.tabToggle.forEach((toggle) => {
        if (toggle !== this) {
          toggle.hide();
        }
      });
    }
  }
}

export default TabToggle;
